import Vue from 'vue'
import VueRouter from 'vue-router'

import store from '@/store/'
import { checkIsRouteWithFeatureEnabled } from '@/utils/feature-flags'

import adminRoutes from './admin'
import { attendanceRoutes } from './attendance'
import { diningHallRoutes } from './dining_hall'
import { disciplineRoutes } from './discipline'
import { infoboardsRoutes } from './infoboards'
import { roomManagementRoutes } from './room-management'
import { settingsRoutes } from './settings'
import { TABS } from './tabs'
import teacherRoutes from './teacher'
import { teachersRoutes } from './teachers'
import { timetableRoutes } from './timetable'
import userRoutes from './user'

Vue.use(VueRouter)

const publicRoutes = [
  {
    path: '',
    component: () => import('@/views/Layouts/MiddleBox.vue'),
    children: [
      {
        path: '',
        name: 'Login',
        component: () => import('@/views/Login/index.vue'),
        meta: {
          public: true,
          title: 'Log in'
        }
      },
      {
        path: 'reset-password',
        component: () => import('@/views/PasswordReset/Reset/index.vue'),
        meta: {
          public: true,
          title: 'Reset password'
        }
      },
      {
        path: 'set-password/:uid/:token',
        name: 'PasswordResetFinish',
        component: () => import('@/views/PasswordReset/Finish/index.vue'),
        meta: {
          public: true,
          title: 'Set a password'
        }
      }
    ]
  },
  {
    path: '/ant-components',
    component: () => import('@/views/AntComponents.vue'),
    name: 'AntComponents',
    meta: {
      public: true
    }
  },
  {
    path: '/leave-request/approve',
    component: () => import('@/views/Attendance/LeaveRequest/Review/index.vue'),
    meta: {
      public: true,
      title: 'Review leave request'
    }
  }
]

const commonRoutes = [
  {
    path: '',
    redirect: '/teacher/timetable',
    component: () => import('@/views/Layouts/RouterView.vue'),
    children: [
      {
        name: 'Students list',
        path: 'students',
        component: () => import('@/views/StudentList/StudentList.vue'),
        meta: {
          title: 'Students list',
          permissions: ['students.view_student']
        }
      },
      {
        name: 'Student details',
        path: 'students/:studentId',
        redirect: '/students/:studentId/details',
        component: () => import('@/views/StudentDetails/index.vue'),
        children: [
          {
            path: 'details',
            component: () => import('@/views/StudentDetails/Details/index.vue'),
            meta: {
              title: 'Student details',
              permissions: ['students.view_student'],
              tab: TABS.academics
            }
          },
          {
            path: 'change-password',
            component: () =>
              import('@/views/StudentDetails/ChangePassword/index.vue'),
            meta: {
              title: 'Student change password',
              permissions: ['students.change_student'],
              tab: TABS.academics
            }
          },
          {
            path: 'edit',
            component: () =>
              import('@/views/StudentDetails/Details/Edit/index.vue'),
            meta: {
              title: 'Student edit',
              permissions: ['students.change_student'],
              tab: TABS.academics
            }
          },
          {
            path: 'courses',
            component: () => import('@/views/StudentDetails/Courses/index.vue'),
            meta: {
              title: 'Student courses',
              permissions: ['grades.view_accgrade'],
              tab: TABS.academics
            }
          },
          {
            path: 'health',
            name: 'Student health details',
            component: () =>
              import('@/views/StudentDetails/Health/StudentHealth.vue'),
            meta: {
              title: 'Health card',
              permissions: ['allergies.view_studentallergy'],
              tab: TABS.academics
            }
          },
          {
            path: 'behaviour',
            redirect: 'behaviour/merit'
          },
          {
            path: 'behaviour/merit',
            name: 'student-behaviour-merit-list',
            component: () =>
              import('@/views/StudentDetails/Behaviour/Merit/index.vue'),
            meta: {
              title: 'Merit',
              permissions: ['merits.view_merit'],
              tab: TABS.academics
            }
          },
          {
            path: 'behaviour/comments',
            component: () =>
              import('@/views/StudentDetails/Behaviour/Comments/index.vue'),
            meta: {
              title: 'Comments',
              permissions: ['permissions.view_studentcomments'],
              tab: TABS.academics
            }
          },
          {
            path: 'attendances',
            component: () =>
              import(
                '@/views/StudentDetails/AcademicAttendances/AcademicAttendances.vue'
              ),
            meta: {
              title: 'Academic Attendances',
              permissions: ['permissions.view_attendance_report'],
              tab: TABS.academics
            }
          },
          {
            path: 'idp-plans',
            name: 'student-idp-plans',
            component: () =>
              import('@/views/StudentDetails/IdpPlanList/IdpPlanList.vue'),
            meta: {
              title: 'Students idp plan',
              permissions: ['students_idp.view_studentidpplan'],
              tab: TABS.academics
            }
          },
          {
            path: 'idp-plans/create',
            component: () =>
              import('@/views/StudentDetails/IdpPlanCreate/index.vue'),
            name: 'idp-create',
            meta: {
              title: 'Create students IDP Plan',
              permissions: ['students_idp.add_studentidpplan'],
              tab: TABS.academics
            }
          },
          {
            path: 'idp-plans/:id',
            component: () =>
              import('@/views/StudentDetails/IdpPlanCreate/index.vue'),
            name: 'idp-edit',
            meta: {
              title: 'Edit students IDP Plan',
              permissions: ['students_idp.change_studentidpplan'],
              tab: TABS.academics
            }
          },
          {
            path: 'idp-plans/:id/details',
            component: () =>
              import('@/views/StudentDetails/IdpPlanDetails/index.vue'),
            name: 'idp-details',
            meta: {
              title: 'Preview IDP Plan',
              permissions: ['students_idp.view_studentidpplan'],
              tab: TABS.academics
            }
          },
          {
            path: 'mission-control',
            name: 'mission-control',
            component: () =>
              import('@/views/StudentDetails/MissionControlDetails/index.vue'),
            meta: {
              title: 'Mission Control',
              permissions: ['mission_control.view_missioncontrol'],
              tab: TABS.academics
            }
          },
          {
            path: 'mission-control/edit-focus',
            component: () =>
              import(
                '@/views/StudentDetails/MissionControlDetails/Focus/Edit/index.vue'
              ),
            meta: {
              title: 'Edit FocusForm',
              permissions: ['mission_control.change_missioncontrol'],
              tab: TABS.academics
            }
          },
          {
            path: 'mission-control/edit-personal',
            component: () =>
              import(
                '@/views/StudentDetails/MissionControlDetails/Personal/Edit/index.vue'
              ),
            meta: {
              title: 'Edit PersonalForm',
              permissions: ['mission_control.change_missioncontrol'],
              tab: TABS.academics
            }
          },
          {
            path: 'mission-control/edit-achievements',
            component: () =>
              import(
                '@/views/StudentDetails/MissionControlDetails/Personal/Edit/AchievementList/index.vue'
              ),
            meta: {
              title: 'Edit Achievements',
              permissions: ['achievements.change_studentachievement'],
              tab: TABS.academics
            }
          },
          {
            path: 'mission-control/edit-internships',
            component: () =>
              import(
                '@/views/StudentDetails/MissionControlDetails/Personal/InternshipEditView.vue'
              ),
            meta: {
              title: 'Edit Internships',
              permissions: ['internships.change_studentinternship'],
              tab: TABS.academics
            }
          },
          {
            path: 'mission-control/edit-tae-projects',
            component: () =>
              import(
                '@/views/StudentDetails/MissionControlDetails/TAE/Edit/ProjectList/index.vue'
              ),
            meta: {
              title: 'Edit TAE Project',
              permissions: ['mission_control.change_taeproject'],
              tab: TABS.academics
            }
          },
          {
            path: 'mission-control/edit-tae-experiences',
            component: () =>
              import(
                '@/views/StudentDetails/MissionControlDetails/TAE/Edit/ExperienceList/index.vue'
              ),
            meta: {
              title: 'Edit TAE Project',
              permissions: ['mission_control.change_studenttaeexperience'],
              tab: TABS.academics
            }
          },
          {
            path: 'mission-control/edit-exams',
            component: () =>
              import(
                '@/views/StudentDetails/MissionControlDetails/Exams/Edit/ExamList/index.vue'
              ),
            meta: {
              title: 'Edit Exams',
              permissions: ['exams.change_studentexternalexam'],
              tab: TABS.academics
            }
          },
          {
            path: 'mission-control/edit-transcripts',
            component: () =>
              import(
                '@/views/StudentDetails/MissionControlDetails/Exams/Edit/TranscriptList/index.vue'
              ),
            meta: {
              title: 'Edit Transcripts',
              permissions: ['exams.change_transcript'],
              tab: TABS.academics
            }
          },
          {
            path: 'mission-control/edit-exam-recommendations',
            component: () =>
              import(
                '@/views/StudentDetails/MissionControlDetails/Exams/Edit/Recommendations/index.vue'
              ),
            meta: {
              title: 'Edit Exam Recommendations',
              permissions: ['exams.change_exam'],
              tab: TABS.academics
            }
          },
          {
            path: 'mission-control/edit-recommendations',
            component: () =>
              import(
                '@/views/StudentDetails/MissionControlDetails/Recommendations/Edit/index.vue'
              ),
            meta: {
              title: 'Edit Recommendations',
              permissions: ['mission_control.change_missioncontrol'],
              tab: TABS.academics
            }
          }
        ]
      },
      {
        name: 'Dashboard',
        path: 'dashboard',
        redirect: '/dashboard/tasks',
        component: () => import('@/views/Dashboard/index.vue'),
        meta: {
          tab: TABS.dashboard
        },
        children: [
          {
            path: 'overview',
            component: () => import('@/views/Dashboard/Overview/index.vue'),
            meta: {
              title: 'Overview',
              tab: TABS.dashboard
              // permissions: []
            }
          },
          {
            path: 'tasks',
            component: () => import('@/views/Dashboard/Tasks/index.vue'),
            meta: {
              title: 'Tasks',
              tab: TABS.dashboard
              // permissions: []
            }
          },
          {
            path: 'calendar',
            redirect: '/dashboard/tasks',
            meta: {
              title: 'Calendar',
              tab: TABS.dashboard
              // permissions: []
            }
          }
        ]
      },
      {
        name: 'CoursesList',
        path: 'courses',
        component: () => import('@/views/CourseList/index.vue'),
        meta: {
          title: 'Courses',
          tab: TABS.academics,
          permissions: ['courses.view_course']
        }
      },
      {
        path: 'health/medicine-plans',
        component: () => import('@/views/Health/MedicineInPlanList/index.vue'),
        name: 'Medicine Plan List',
        meta: {
          title: 'Medicine Plans List',
          tab: TABS.boarding,
          permissions: ['sick_reports.list_sickreport']
        }
      },
      {
        path: 'health/health-checks',
        component: () =>
          import('@/views/Health/HealthCheckList/HealthCheckListView.vue'),
        name: 'Health Checks List',
        meta: {
          title: 'Health Checks List',
          tab: TABS.boarding,
          permissions: ['health_checks.view_healthcheck']
        }
      },
      {
        path: 'health/sick-reports',
        component: () => import('@/views/SickReportList/index.vue'),
        name: 'Sick Report List',
        meta: {
          title: 'Sick Reports List',
          tab: TABS.boarding,
          permissions: ['sick_reports.list_sickreport']
        }
      },
      {
        path: 'health/sick-reports/create',
        component: () => import('@/views/SickReportCreate/index.vue'),
        name: 'Sick Report Create',
        meta: {
          title: 'Create Sick Report',
          tab: TABS.boarding,
          permissions: ['sick_reports.add_sickreport']
        }
      },
      {
        path: 'health/sick-reports/:id',
        name: 'sick-report-details',
        redirect: '/health/sick-reports/:id/details/',
        component: () => import('@/views/SickReportDetails/index.vue'),
        children: [
          {
            path: 'details',
            component: () =>
              import('@/views/SickReportDetails/SickReportDetails.vue'),
            meta: {
              title: 'Sick Report Details',
              permissions: ['sick_reports.view_sickreport'],
              tab: TABS.boarding
            }
          },
          {
            path: 'absences',
            component: () =>
              import(
                '@/views/SickReportDetails/SickReportStudentsAbsences/index.vue'
              ),
            meta: {
              title: 'Sick Report Details',
              permissions: ['sick_reports.view_sickreport'],
              tab: TABS.boarding
            }
          },
          {
            path: 'health-check-history',
            component: () =>
              import(
                '@/views/SickReportDetails/SickReportHealthCheckHistory/index.vue'
              ),
            meta: {
              title: 'Sick Report Details',
              permissions: ['sick_reports.view_sickreport'],
              tab: TABS.boarding
            }
          },
          {
            path: 'comments',
            component: () =>
              import('@/views/SickReportDetails/SickReportComments/index.vue'),
            meta: {
              title: 'Sick Report Comments',
              permissions: ['sick_reports.view_sickreportcomment'],
              tab: TABS.boarding
            }
          }
        ]
      },

      {
        path: 'cancelled-lessons',
        component: () => import('@/views/CancelledLessons/index.vue'),
        meta: {
          title: 'Cancelled lessons',
          tab: TABS.academics,
          permissions: ['lesson_cancellation.view_cancellesson']
        }
      },
      {
        path: 'school-programmes',
        name: 'School Programme List',
        component: () =>
          import(
            '@/views/Boarding/SchoolProgrammes/SchoolProgrammesList/index.vue'
          ),
        meta: {
          title: 'School Programmes',
          tab: TABS.boarding,
          permissions: ['school_programmes.view_schoolprogramme']
        }
      },
      {
        path: 'school-programmes/add',
        name: 'Create School Programme',
        component: () =>
          import(
            '@/views/Boarding/SchoolProgrammes/SchoolProgrammesForm/SchoolProgrammesForm.vue'
          ),
        meta: {
          title: 'Create School Programme',
          tab: TABS.boarding,
          permissions: ['school_programmes.add_schoolprogramme']
        }
      },
      {
        path: 'school-programmes/:id',
        name: 'Edit School Programme',
        component: () =>
          import(
            '@/views/Boarding/SchoolProgrammes/SchoolProgrammesForm/SchoolProgrammesForm.vue'
          ),
        meta: {
          title: 'Edit School Programme',
          tab: TABS.boarding,
          permissions: ['school_programmes.change_schoolprogramme']
        }
      },
      {
        name: 'RequestedCourseList',
        path: 'requested-courses',
        component: () => import('@/views/RequestedCourseList/index.vue'),
        meta: {
          title: 'Requested Courses',
          tab: TABS.academics,
          permissions: ['courses.add_course']
        }
      },
      {
        name: 'Course details',
        path: 'courses/:courseId',
        component: () => import('@/views/CourseDetails/index.vue'),
        children: [
          {
            path: 'details',
            component: () => import('@/views/CourseDetails/Details/index.vue'),
            meta: {
              title: 'Course details',
              tab: TABS.academics,
              permissions: ['courses.view_course']
            }
          },
          {
            path: 'students',
            component: () => import('@/views/CourseDetails/Students/index.vue'),
            meta: {
              title: 'Course students',
              tab: TABS.academics,
              permissions: ['courses.view_course']
            }
          }
        ]
      },
      {
        name: 'Alerts',
        path: 'alerts',
        component: () => import('@/views/Alerts/index.vue'),
        meta: {
          tab: TABS.academics
        },
        children: [
          {
            path: 'attendances',
            component: () =>
              import('@/views/Alerts/AcademicAttendance/index.vue'),
            meta: {
              title: 'Academic Attendances',
              tab: TABS.academics,
              permissions: ['attendances.view_academicattendancealert']
            }
          }
        ]
      },
      {
        name: 'Progress Alerts',
        path: 'progress',
        component: () => import('@/views/ProgressAlerts/index.vue'),
        meta: {
          tab: TABS.academics
        },
        children: [
          {
            path: 'current-potential-grades',
            component: () =>
              import('@/views/ProgressAlerts/CurrentTargetGrades/index.vue'),
            meta: {
              title: 'Current potential grades',
              tab: TABS.academics,
              permissions: ['permissions.view_potentialgradealert']
            }
          },
          {
            path: 'progress-comments',
            component: () =>
              import('@/views/ProgressAlerts/ProgressCommentList/index.vue'),
            meta: {
              title: 'Potential grades weekly report',
              tab: TABS.academics,
              permissions: ['permissions.view_progress_comments_summary_view']
            }
          }
        ]
      },
      {
        name: 'IDP Plans list',
        path: 'idp-plans/',
        component: () => import('@/views/IdpPlansList/index.vue'),
        meta: {
          title: 'IDP Plans',
          tab: TABS.academics,
          permissions: ['students_idp.view_studentidpplan']
        }
      },
      {
        name: 'Timetable Preview',
        path: 'timetable-preview/',
        component: () => import('@/views/TeacherPanel/TimetablePreview.vue'),
        meta: {
          title: 'Timetable Preview',
          tab: TABS.academics,
          permissions: [
            'draft_activities.view_draftactivity',
            'rota_activities.view_draftrotaactivity'
          ]
        }
      },
      {
        name: 'Timetable planner',
        path: 'planner/',
        component: () => import('@/views/Planner/index.vue'),
        meta: {
          permissions: ['remote_generator.view_generationtask'],
          tab: TABS.administration
        },
        children: [
          {
            path: 'timetables',
            component: () => import('@/views/Planner/TimetableList/index.vue'),
            name: 'timetable-list',
            meta: {
              title: 'Timetables & Boarding Rotas',
              tab: TABS.administration,
              permissions: ['timetable.view_timetable']
            }
          },
          {
            path: 'courses',
            component: () => import('@/views/Planner/CourseList/index.vue'),
            meta: {
              title: 'Courses list',
              tab: TABS.administration,
              permissions: ['courses.view_course']
            }
          },
          {
            path: 'sections',
            component: () => import('@/views/Planner/SectionList/index.vue'),
            name: 'sections-list',
            meta: {
              title: 'Sections list',
              tab: TABS.administration,
              permissions: ['sections.view_section']
            }
          },
          {
            path: 'rooms',
            component: () => import('@/views/Planner/RoomList/index.vue'),
            meta: {
              title: 'Rooms list',
              tab: TABS.administration,
              permissions: ['rooms.view_room']
            }
          },
          {
            path: 'rooms/add',
            component: () => import('@/views/Planner/RoomForm/index.vue'),
            meta: {
              title: 'Room create',
              tab: TABS.administration,
              permissions: ['rooms.view_room']
            }
          },
          {
            path: 'rooms/:roomId',
            component: () => import('@/views/Planner/RoomForm/index.vue'),
            meta: {
              title: 'Room edit',
              tab: TABS.administration,
              permissions: ['rooms.view_room']
            }
          },
          {
            path: 'courses/add',
            component: () => import('@/views/Planner/CourseForm/index.vue'),
            meta: {
              title: 'Course create',
              tab: TABS.administration,
              permissions: ['remote_generator.view_generationtask']
            }
          },
          {
            path: 'courses/:courseId',
            component: () => import('@/views/Planner/CourseForm/index.vue'),
            meta: {
              title: 'Course edit',
              tab: TABS.administration,
              permissions: ['remote_generator.view_generationtask']
            }
          }
        ]
      },
      {
        path: 'planner/timetables/create',
        name: 'timetable-create',
        component: () => import('@/views/Planner/TimetableForm/'),
        meta: {
          title: 'Timetable create',
          tab: TABS.administration,
          permissions: ['remote_generator.view_generationtask']
        }
      },
      {
        path: 'timetables/:timetableId/optimizations/:rotaId?',
        name: 'timetable-optimizations',
        component: () => import('@/views/Planner/PotentialOptimizations/'),
        meta: {
          title: 'Timetable potential optimizations',
          tab: TABS.administration,
          permissions: ['remote_generator.view_generationtask']
        }
      },
      {
        path: 'timetables/:timetableId/free-slots/',
        component: () => import('@/views/Planner/FreeSlots/'),
        name: 'timetable-free-slots',
        meta: {
          title: 'Timetable free slots',
          tab: TABS.administration,
          permissions: ['draft_activities.view_draftactivity']
        }
      },
      {
        path: 'timetables/:timetableId/rooms-import/',
        name: 'timetable-rooms-import',
        component: () => import('@/views/Planner/RoomsImport/'),
        meta: {
          title: 'Import rooms from file',
          tab: TABS.administration,
          permissions: ['draft_activities.change_draftactivity']
        }
      },

      {
        path: 'timetables/generation/:generationTaskId',
        component: () => import('@/views/Planner/GenerationProcess/'),
        name: 'generation-process',
        meta: {
          title: 'Timetable generation process',
          tab: TABS.administration,
          permissions: ['remote_generator.view_generationtask']
        }
      },
      ...timetableRoutes,
      {
        path: 'planner/sections/add',
        name: 'add-section',
        component: () =>
          import('@/views/Planner/SectionForm/SectionFormView.vue'),
        meta: {
          title: 'Section create',
          tab: TABS.administration,
          permissions: ['remote_generator.view_generationtask']
        }
      },
      {
        path: 'planner/sections/:sectionId',
        name: 'edit-section',
        component: () =>
          import('@/views/Planner/SectionForm/SectionFormView.vue'),
        meta: {
          title: 'Section edit',
          tab: TABS.administration,
          permissions: ['remote_generator.view_generationtask']
        }
      },
      {
        path: 'houses/add',
        component: () => import('@/views/HouseForm/index.vue'),
        meta: {
          title: 'Create house',
          tab: TABS.administration,
          permissions: ['houses.add_house']
        }
      },
      {
        path: 'houses/:houseId',
        component: () => import('@/views/HouseForm/index.vue'),
        meta: {
          title: 'Edit house',
          tab: TABS.administration,
          permissions: ['houses.change_house']
        }
      },
      {
        path: 'houses',
        component: () => import('@/views/HouseList/index.vue'),
        meta: {
          title: 'Houses list',
          tab: TABS.administration,
          permissions: ['houses.change_house']
        }
      },
      {
        path: 'files-for-students',
        component: () =>
          import('@/views/DocumentsForApps/DocumentsForAppsView.vue'),
        meta: {
          title: 'Documents for apps list',
          tab: TABS.administration,
          permissions: ['files_for_students.view_fileforstudents']
        }
      },
      {
        path: 'billing-reports',
        name: 'billing-report-list',
        component: () => import('@/views/AdminPanel/BillingReports/index.vue'),
        meta: {
          title: 'Billing reports list',
          tab: TABS.administration,
          permissions: ['billings.view_billingreport']
        }
      },
      {
        path: 'billing-reports/add',
        name: 'add-billing-report',
        component: () =>
          import(
            '@/views/AdminPanel/BillingReports/AddBillingReport/index.vue'
          ),
        meta: {
          title: 'Add billing report',
          tab: TABS.administration,
          permissions: ['billings.add_billingreport']
        }
      },
      {
        path: 'billing-reports/:id',
        name: 'billing-report-details',
        component: () =>
          import('@/views/AdminPanel/BillingReports/BillingReportDetail.vue'),
        meta: {
          title: 'Billing report details',
          tab: TABS.administration,
          permissions: ['billings.view_billingreport']
        }
      },
      {
        path: 'teams',
        name: 'team-list',
        component: () => import('@/views/Teams/TeamsView.vue'),
        meta: {
          title: 'Teams',
          tab: TABS.administration,
          permissions: ['teams.view_team']
        }
      },
      {
        path: 'health/doctors',
        component: () => import('@/views/Health/DoctorList/DoctorListView.vue'),
        name: 'Doctor List',
        meta: {
          title: 'Doctors List',
          tab: TABS.administration,
          permissions: ['doctors.view_doctor', 'doctors.change_doctor']
        }
      },
      {
        path: 'exam-planner/exam-sessions',
        component: () =>
          import('@/views/ExamPlanner/ExamSessionList/index.vue'),
        name: 'Exam Session List',
        meta: {
          title: 'Exam planner',
          tab: TABS.administration,
          permissions: ['exam_planner.view_examsession']
        }
      },
      {
        path: 'exam-planner/calendar/:id',
        component: () => import('@/views/ExamPlanner/ExamsPreview/index.vue'),
        name: 'Exam Session Timetable',
        meta: {
          title: 'Exam timetable',
          tab: TABS.administration,
          permissions: ['exam_planner.view_examsession']
        }
      },
      {
        path: 'exam-planner/exam-sessions/:id',
        component: () =>
          import('@/views/ExamPlanner/ExamSessionDetails/index.vue'),
        name: 'Exam Session Details',
        meta: {
          title: 'Exam planner',
          tab: TABS.administration,
          permissions: [
            'exam_planner.view_examsession',
            'exam_planner.view_exam'
          ]
        }
      },
      {
        path: 'exam-planner/exam-attendance/:examSession/:exam',
        component: () => import('@/views/ExamPlanner/ExamAttendance/index.vue'),
        name: 'ExamAttendance',
        meta: {
          title: 'Exam attendance',
          tab: TABS.academics
        }
      },
      {
        path: 'school-programmes/:programmeId/attendance-reports',
        component: () =>
          import(
            '@/views/Boarding/SchoolProgrammes/ProgrammeAttendance/ProgrammeAttendanceCollapse.vue'
          ),
        name: 'ProgrammeAttendance',
        meta: {
          title: 'Programme attendance',
          tab: TABS.academics
        }
      },
      ...attendanceRoutes,
      ...teachersRoutes,
      ...diningHallRoutes,
      ...disciplineRoutes,
      ...infoboardsRoutes,
      ...settingsRoutes,
      ...roomManagementRoutes
    ]
  }
]

const privateRoutes = [
  {
    path: '',
    redirect: '/user/my-timetable',
    component: () => import('@/views/Layouts/SidebarContent.vue'),
    children: [...commonRoutes, ...userRoutes, ...teacherRoutes, ...adminRoutes]
  }
]

const router = new VueRouter({
  mode: 'history',
  base: import.meta.env.BASE_URL,
  routes: [...publicRoutes, ...privateRoutes]
})

const routeGuard = async (to, from, next) => {
  document.title = to.meta.title ? `${to.meta.title} — Rosenberg` : 'Rosenberg'

  const publicRoute = to.meta.public

  if (publicRoute) {
    next()
    return
  }

  const { profiles } = store.state.userInfo
  const { permissions } = store.state.userInfo

  if (!publicRoute && !profiles) {
    Vue.prototype.$notification.error({
      message: 'This page is only for logged in users. Log in!'
    })
    next({ name: 'Login', query: { next: to.path } })
    return
  }

  const routePermissions = to.meta?.permissions
  if (routePermissions) {
    const hasPermission = routePermissions.some(routePermission =>
      permissions.includes(routePermission)
    )

    if (!hasPermission || !checkIsRouteWithFeatureEnabled(to)) {
      Vue.prototype.$notification.error({
        message: "You don't have permission to view this page."
      })
      next({ name: 'Login' })
      return
    }
  }

  next()
}

router.beforeEach(routeGuard)

export default router
